// R58-L-08 + R58-L-09 — Promote controller.
//
// Glue between selection-UI (Tiles widget, FindingsPanel, BBoxOverlay)
// and the lyse@5.8.0 bulk endpoints. The controller decides whether the
// selection promotes silently (mapper-driven, L-08) or opens the manual
// batch modal (L-09) based on L-08's `needsManual` array.
//
// History: in L-10 this was a stub that captured calls on
// `window.__r58l10PromoteCalls` so the Tiles spec could assert the
// payload reached the controller without a live BE. The L-08 stories'
// dispatch swap (this file, now) replaces the stub body with the real
// mutation hook. The window-capture log is preserved so the L-10
// acceptance spec keeps passing.

import type {
  BulkPromoteAiFindingsResponse,
  GeometryOverride,
} from "@aeriosiq/types";

const WINDOW_KEY = "__r58l10PromoteCalls";

declare global {
  interface Window {
    /** R58-L-10 boundary log — see comment above. */
    [WINDOW_KEY]?: { findingIds: string[]; ts: number }[];
    /** R58-L-08 result echo for the e2e spec. */
    __r58l08LastResponse?: BulkPromoteAiFindingsResponse | null;
  }
}

export interface PromoteManyArgs {
  /** AI-finding ids the user selected. */
  findingIds: string[];
  /** Inspection / data-acquisition id (carried through for tenant
   *  scoping; the BE bulk endpoint reads it transitively via the
   *  finding rows). */
  inspectionId: string;
}

/**
 * Captures the call for the L-10 acceptance spec. Always invoked,
 * regardless of whether a live mutator is wired (legacy behaviour the
 * L-10 spec still pins).
 */
function captureCall(findingIds: string[]): void {
  if (typeof window !== "undefined") {
    const w = window;
    w[WINDOW_KEY] ??= [];
    w[WINDOW_KEY].push({ findingIds: [...findingIds], ts: Date.now() });
  }
  if (typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.info("[R58-L-08] PromoteController.promoteMany", {
      findingIds,
    });
  }
}

/**
 * Static surface preserved for L-10's acceptance spec — emits the
 * capture-log only. L-08-aware callers should use
 * {@link runMapperPromote} (below) which actually invokes the mutation.
 */
export const PromoteController = {
  promoteMany({ findingIds }: PromoteManyArgs): void {
    if (findingIds.length === 0) return;
    captureCall(findingIds);
  },
};

/**
 * Live mapper-promote entry point — wires {@link captureCall} together
 * with the L-08 mutation. The caller (a React component) passes the
 * mutation handle (`usePromoteWithMapper(inspectionId).mutateAsync`) and
 * an `openManualModal` callback that the controller invokes when the
 * response's `needsManual` array is non-empty.
 */
export interface RunMapperPromoteArgs extends PromoteManyArgs {
  /** `usePromoteWithMapper(inspectionId).mutateAsync`. */
  mutateAsync: (vars: {
    findingIds: string[];
    geometryOverrides?: Record<string, GeometryOverride>;
  }) => Promise<BulkPromoteAiFindingsResponse>;
  /** Called with the `needsManual` array (and the response promoted set
   *  for FE-side coordination — e.g. snackbar copy). The handler usually
   *  opens the L-09 batch modal. */
  openManualModal: (needsManual: BulkPromoteAiFindingsResponse) => void;
  /**
   * ANA-R3-GEOM-05 — OPTIONAL per-finding geometry overrides (PIXEL paths),
   * keyed by findingId. Built from the session override store by the caller
   * (normalised → × image dims). Each key MUST be in `findingIds`; absent ⇒
   * the unchanged mapper-driven behaviour. The created defect's
   * `geometry_origin` is server-derived (`model_adjusted` when overridden).
   */
  geometryOverrides?: Record<string, GeometryOverride>;
}

export async function runMapperPromote(
  args: RunMapperPromoteArgs,
): Promise<BulkPromoteAiFindingsResponse | null> {
  const { findingIds, mutateAsync, openManualModal, geometryOverrides } = args;
  if (findingIds.length === 0) return null;
  captureCall(findingIds);

  const hasOverrides =
    geometryOverrides && Object.keys(geometryOverrides).length > 0;
  const data = await mutateAsync({
    findingIds,
    ...(hasOverrides ? { geometryOverrides } : {}),
  });
  if (typeof window !== "undefined") {
    window.__r58l08LastResponse = data;
  }
  if (data.needsManual.length > 0) {
    openManualModal(data);
  }
  return data;
}
